/**
 * BulkPlacementAction — botón «Ubicación / rack» de la barra de selección,
 * con su diálogo y el resumen del resultado.
 *
 * Se usa como contenido de `bulkActions` de `ListPage`:
 *
 *   bulkActions={({ selectedRows }) => (
 *     <BulkPlacementAction
 *       companyId={companyId}
 *       entity="elements"          // clave del backend
 *       supportsRack
 *       selectedRows={selectedRows}
 *       onApplied={() => { recargar(); setBulkResetKey((k) => k + 1); }}
 *     />
 *   )}
 *
 * El resumen se pinta como diálogo aparte para que se vea aunque la barra de
 * selección desaparezca justo después de aplicar el cambio.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BulkPlacementDialog from './BulkPlacementDialog';
import Dialog from './Dialog';

export default function BulkPlacementAction({
  companyId,
  entity,
  supportsRack = true,
  selectedRows = [],
  label,
  onApplied,
}) {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  const [rows, setRows] = useState(null);       // selección congelada al abrir
  const [result, setResult] = useState(null);   // resumen tras aplicar

  /* OJO: el resumen se pinta ANTES de avisar al listado. Si se avisara al
     aplicar, la pantalla vaciaría la selección, `bulkActions` dejaría de
     pintarse y este componente se desmontaría con el resumen dentro. */
  const closeResult = () => {
    const payload = result;
    setResult(null);
    onApplied?.(payload);
  };

  return (
    <>
      <button
        type="button"
        className="list-btn-primary"
        onClick={() => setRows(selectedRows)}
        disabled={!selectedRows.length}
        title={tr('bulk.open_hint', 'Asignar ubicación y rack a los elementos seleccionados')}
      >
        <span aria-hidden="true">📍</span> {label || tr('bulk.open', 'Ubicación / rack')}
      </button>

      {rows && (
        <BulkPlacementDialog
          companyId={companyId}
          entity={entity}
          rows={rows}
          supportsRack={supportsRack}
          onClose={() => setRows(null)}
          onDone={(payload) => {
            setRows(null);
            setResult(payload || { updated: 0, failed: [] });
          }}
        />
      )}

      {result && (
        <Dialog
          title={tr('bulk.done_title', 'Cambio masivo aplicado')}
          icon={result.failed?.length ? '⚠️' : '✅'}
          onClose={closeResult}
          maxWidth={520}
          footer={(
            <button type="button" className="list-btn-primary" onClick={closeResult}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          <p>
            <strong>{result.updated}</strong>{' '}
            {result.updated === 1
              ? tr('bulk.done_row', 'elemento actualizado')
              : tr('bulk.done_rows', 'elementos actualizados')}
            {result.failed?.length > 0 && (
              <> · <strong style={{ color: 'var(--red-600)' }}>{result.failed.length}</strong>{' '}
                {result.failed.length === 1
                  ? tr('bulk.done_failed_one', 'no se pudo cambiar')
                  : tr('bulk.done_failed', 'no se pudieron cambiar')}</>
            )}
          </p>
          {result.failed?.length > 0 && (
            <ul className="list-hint" style={{ marginTop: 8, paddingLeft: 18 }}>
              {result.failed.slice(0, 10).map((item) => (
                <li key={item.id}>#{item.id}: {item.error}</li>
              ))}
              {result.failed.length > 10 && <li>…</li>}
            </ul>
          )}
        </Dialog>
      )}
    </>
  );
}
